import React, { useEffect, useRef } from "react";

import { FLOW, PlanRef, planRef } from "./flowCopy";
import { aeName } from "./PlanCard";
import PlanCoverageCard from "./PlanCoverageCard";
import { planFlow } from "./planFlow";
import PlanFreshnessCard from "./PlanFreshnessCard";
import PlanLinksCard, { LINKS_INTRO } from "./PlanLinksCard";
import PlanMeddpiccCard from "./PlanMeddpiccCard";
import PlanPipelineCard from "./PlanPipelineCard";
import PlanSectionOwnersCard from "./PlanSectionOwnersCard";
import PlanVersionsCard from "./PlanVersionsCard";
import { NextStepCard, PlanNav, STEP_EYEBROW, stepHeading } from "./shared";
import type { AccountPlanOverview } from "../../Types/AccountPlan";
import { STALLED_LINE } from "../../Utils/AccountPlans/queueText";

interface Props {
  accountId: string;
  data: AccountPlanOverview;
  nav: PlanNav;
  onChanged: () => void;
  onOpenViewer: (fileId: string) => void;
  /** Opened by "Go to the problem" (?focus=problem): show the newest upload's problem. */
  focusProblem?: boolean;
  /** The header's "Upload new version" (?focus=upload): scroll to the drop zone. */
  focusUpload?: boolean;
  onUploadFocused?: () => void;
  /** Version history: the read-only published briefing / the draft under review. */
  onOpenPublished?: () => void;
  onOpenDraft?: () => void;
  /** Deep link `replace=1` (the Plans panel's "Replace this draft"): open its confirm. */
  startReplace?: boolean;
  /** Deep link `progress=<file id>` (the processing pill): reopen that upload's progress. */
  reopenProgress?: string | null;
  onProgressReopened?: () => void;
}

/**
 * Step 1 · Upload (mock v4 "Manage plan"). AE authors; CSM reads. One place to
 * act (STP-12): no quick buttons to other steps here (the stepper is the
 * navigation) and a single next-step card that only moves to a step; commit
 * buttons such as Generate live inside their own step.
 */
const PlanOverviewTab: React.FC<Props> = ({
  accountId,
  data,
  nav,
  onChanged,
  onOpenViewer,
  focusProblem = false,
  focusUpload = false,
  onUploadFocused,
  onOpenPublished,
  onOpenDraft,
  startReplace = false,
  reopenProgress = null,
  onProgressReopened,
}) => {
  const dropzoneRef = useRef<HTMLDivElement>(null);
  const canEdit = nav.canAct("AE");
  const roleLabel = nav.persona === "CSM" ? "CSM" : "AE";
  const ae = aeName(data);

  const current = data.files.find(f => f.id === data.current_file_id) || null;
  const processing = data.files.find(f => f.status === "processing") || null;
  const flow = planFlow(data);
  // Q9 (Sushant, 2026-10-31): with nothing published, anyone but the AE never sees
  // an upload that stopped (its reason or its file name), only that the AE is on it.
  const hideStopped = !canEdit && !data.files.some(f => f.published);
  const shownFiles = hideStopped
    ? data.files.filter(
        f =>
          f.confirmed !== false ||
          !["failed", "rejected", "needs_review"].includes(f.status)
      )
    : data.files;

  const focusDropzone = () => {
    const el = dropzoneRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    const target = el.querySelector<HTMLElement>(
      "[tabindex='0'], button, input"
    );
    target?.focus({ preventScroll: true });
  };

  useEffect(() => {
    if (!focusUpload) return;
    focusDropzone();
    onUploadFocused?.();
    // Only when the header asks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusUpload]);

  let next: {
    text: string;
    action?: { label: string; onClick: () => void };
    notAStep?: boolean;
  };
  if (processing) {
    next = { text: "Parsing the slides… this usually takes under a minute." };
  } else if (!current) {
    next = canEdit
      ? {
          // No button: the drop zone is right there (upload-screen review, 2 Nov).
          text: "Upload the account plan. ExecBridge reads it, you confirm it's the right plan, and the draft briefing is written for your review.",
        }
      : {
          text: "Waiting on the AE to upload the first plan.",
          action: { label: FLOW.backToPlans, onClick: nav.backToAccounts },
          notAStep: true,
        };
  } else if (!flow.currentConfirmed) {
    // Confirm plan (Phase 2): nothing is written until the AE accepts the upload.
    next = canEdit
      ? {
          text: `${current.filename} has been read. Check it's the right plan and confirm it; the draft briefing and Briefing Questions are written once you do.`,
          action: {
            label: FLOW.confirmThePlan,
            onClick: () => nav.go("viewer"),
          },
        }
      : {
          text: `${FLOW.waitingConfirmPlan}. You're notified when the draft briefing needs your sections.`,
          // F-UF-F18: the CSM reads the upload; confirming is the AE's.
          action: {
            label: "View the upload",
            onClick: () => nav.go("viewer"),
          },
        };
  } else if (flow.briefing) {
    // Being written, or stalled for an Izba admin (UI tour A item 1): nothing to
    // review or publish yet, so no action.
    next = {
      text:
        flow.briefing === "stalled"
          ? `${STALLED_LINE}. The draft briefing for ${planRef(current)} isn't ready yet.`
          : `Writing the draft briefing for ${planRef(current)}. You're notified when it's ready to review.`,
    };
  } else if (!data.current_review) {
    // Confirmed before the Confirm step started the briefing: "Write it" is on Review briefing.
    next = {
      text: `The draft briefing for ${planRef(current)} hasn't been written yet.`,
      action: {
        label: "Review briefing",
        onClick: () => nav.go("sponsor"),
      },
    };
  } else if (flow.currentPublished) {
    next = canEdit
      ? {
          text: `${PlanRef(current)} is published. Upload a new version when the plan changes.`,
          action: { label: "Upload new version", onClick: focusDropzone },
        }
      : {
          text: `${PlanRef(current)} is published. Nothing for you right now; you're notified when a new draft needs your sections.`,
        };
  } else {
    next = {
      text: "Review the briefing the sponsor sees; every claim links back to a slide.",
      action: {
        label: "Review briefing",
        onClick: () => nav.go("sponsor"),
      },
    };
  }

  return (
    <div>
      <div className={`mb-5 ${STEP_EYEBROW}`}>{stepHeading("AE", "plan")}</div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="min-w-0 space-y-5 lg:col-span-2">
          <PlanVersionsCard
            accountId={accountId}
            files={shownFiles}
            currentFileId={data.current_file_id}
            sponsorFileId={data.sponsor_file_id}
            canEdit={canEdit}
            viewOnly={!!data.me?.read_only}
            canDelete={canEdit && !data.me?.admin}
            aeLabel={ae ? `${ae} (AE)` : "The AE"}
            onChanged={onChanged}
            onOpenViewer={onOpenViewer}
            dropzoneRef={dropzoneRef}
            focusProblem={focusProblem}
            onOpenPublished={onOpenPublished}
            onOpenDraft={onOpenDraft}
            reviews={[
              data.current_review,
              data.published_review,
              data.sponsor_review,
            ]}
            rejectedUploads={hideStopped ? [] : data.rejected_uploads}
            pendingDraft={data.pending_draft}
            startReplace={startReplace}
            reopenProgress={reopenProgress}
            onProgressReopened={onProgressReopened}
            linksSlot={
              // Links aren't supported yet: shown greyed out and unclickable.
              <section aria-label="Links to the plan file (coming soon)">
                {/* The badge has its own space beside the intro (UI tour,
                    2026-11-03: absolutely placed, it covered "ExecBridge"). */}
                <div className="mb-2 flex items-start justify-between gap-3">
                  <p className="min-w-0 text-xs font-medium text-gray-400">
                    {LINKS_INTRO}
                  </p>
                  <span
                    data-testid="links-coming-soon"
                    title="Linking the plan file or folder isn't available yet. Upload the file above."
                    className="shrink-0 whitespace-nowrap rounded-full border border-gray-200 bg-white px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wide text-gray-500"
                  >
                    Coming soon
                  </span>
                </div>
                <div
                  inert
                  aria-hidden
                  className="pointer-events-none select-none opacity-50 grayscale"
                >
                  <PlanLinksCard
                    bare
                    hideIntro
                    accountId={accountId}
                    links={data.links}
                    canEdit={canEdit}
                    onChanged={onChanged}
                  />
                </div>
              </section>
            }
          />
          <PlanSectionOwnersCard
            accountId={accountId}
            data={data}
            canEdit={canEdit}
            onChanged={onChanged}
          />
          <PlanMeddpiccCard
            accountId={accountId}
            currentFileId={data.current_file_id}
            canEdit={canEdit}
          />
        </div>

        <div className="min-w-0 space-y-4">
          <PlanPipelineCard data={data} nav={nav} />

          <PlanFreshnessCard
            freshness={data.freshness}
            refreshDays={data.settings.refresh_days}
          />

          <PlanCoverageCard
            accountId={accountId}
            currentFileId={data.current_file_id}
            hasDraft={!!data.current_review}
            onOpenQuestions={() => nav.go("qa")}
          />
        </div>
      </div>

      {/* The Upload tab shows no next-step card (upload-screen review, 2 Nov: it
          repeated the page); its action stays on the header's next-step button. */}
      <NextStepCard
        label={`${roleLabel} next step`}
        action={next.action}
        headerOnly={canEdit}
        notAStep={next.notAStep}
      >
        {next.text}
      </NextStepCard>
    </div>
  );
};

export default PlanOverviewTab;
